<template>
  <div class="buy-and-cancel-order-details">
    <div class="row g-2 g-lg-3 mb-3">
      <div class="col-xl-4">
        <div class="details-panel details-panel-summary h-100">
          <div class="d-flex align-items-center mb-2">
            <FontAwesomeIcon icon="info-circle" class="text-primary me-2" />
            <span class="text-uppercase text-muted fw-semibold section-label">
              {{ $t("company_orders.buy.order_summary") }}
            </span>
          </div>

          <div class="summary-content">
            <div class="text-muted small mb-1">
              {{ $t("bulk_buy.volume") }}
            </div>
            <div class="fs-2 fw-semibold lh-sm">
              {{ details.volume }}
              <span class="fs-6 text-muted fw-normal">MWh</span>
            </div>

            <template v-if="hasPricingInfo">
              <div class="summary-detail">
                <div
                  class="text-muted small text-uppercase summary-detail-label"
                >
                  {{ $t("bulk_buy.total_price") }}
                </div>
                <div class="summary-detail-value">
                  €{{ formatCurrency(details.quoted_total_price, 2) }}
                </div>
              </div>

              <div class="summary-detail">
                <div
                  class="text-muted small text-uppercase summary-detail-label"
                >
                  {{ $t("bulk_buy.price_per_mwh") }}
                </div>
                <div class="summary-detail-value">€{{ pricePerMwh }}/MWh</div>
              </div>
            </template>

            <template v-else>
              <div v-if="showRfqTimeline" class="mt-3">
                <p class="text-muted small mb-2">
                  {{ $t("bulk_buy.rfq_notice") }}
                </p>
                <RfqStatusTimeline :createdAt="orderCreatedAt!" />
              </div>
              <p v-else class="text-muted small mt-3 mb-0">
                {{ $t("bulk_buy.no_pricing_info") }}
              </p>
            </template>
          </div>
        </div>
      </div>

      <div class="col-xl-8">
        <div class="details-panel h-100">
          <div class="d-flex align-items-center mb-2">
            <FontAwesomeIcon icon="building" class="text-primary me-2" />
            <span class="text-uppercase text-muted fw-semibold section-label">
              {{ $t("bulk_buy.destination_companies") }}
            </span>
          </div>

          <div class="row g-2">
            <div class="col-md-6">
              <div class="detail-item h-100">
                <div class="text-muted small mb-1">
                  {{ $t("bulk_buy.company_name") }}
                </div>
                <div class="fw-semibold">
                  {{ details.beneficiary_name || "—" }}
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="detail-item h-100">
                <div class="text-muted small mb-1">
                  {{ $t("bulk_buy.country") }}
                </div>
                <span class="badge bg-light text-dark border">
                  {{ details.residency }}
                </span>
              </div>
            </div>

            <div class="col-md-6">
              <div class="detail-item h-100">
                <div class="text-muted small mb-1">
                  {{ $t("bulk_buy.eac_type") }}
                </div>
                <div class="fw-semibold">
                  {{ details.eac_type || "—" }}
                </div>
              </div>
            </div>

            <div v-if="details.registry_code" class="col-md-6">
              <div class="detail-item h-100">
                <div class="text-muted small mb-1">
                  {{ $t("bulk_buy.registry_code") }}
                </div>
                <div class="detail-value font-monospace">
                  {{ details.registry_code }}
                </div>
              </div>
            </div>

            <div :class="details.registry_code ? 'col-md-6' : 'col-md-12'">
              <div class="detail-item h-100">
                <div class="text-muted small mb-1">
                  {{ $t("bulk_buy.company_address") }}
                </div>
                <div class="fw-semibold">
                  {{ details.address || "—" }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-2 g-lg-3 mb-3">
      <div class="col-xl-6">
        <div class="details-panel h-100">
          <div class="text-uppercase text-muted fw-semibold mb-3 section-label">
            {{ $t("bulk_buy.purchase_settings_title") }}
          </div>

          <div class="row g-2">
            <div
              v-for="item in purchaseSettingItems"
              :key="item.label"
              class="col-md-6"
            >
              <div class="detail-item h-100">
                <div class="text-muted small mb-1">
                  {{ item.label }}
                </div>
                <div class="fw-semibold">
                  {{ item.value }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-6">
        <div class="details-panel h-100">
          <div class="text-uppercase text-muted fw-semibold mb-3 section-label">
            {{ $t("bulk_buy.cancellation_settings") }}
          </div>

          <div class="row g-2">
            <div
              v-for="item in cancellationSettingItems"
              :key="item.label"
              class="col-md-6"
            >
              <div class="detail-item h-100">
                <div class="text-muted small mb-1">
                  {{ item.label }}
                </div>
                <div class="fw-semibold">
                  {{ item.value }}
                </div>
              </div>
            </div>

            <div
              v-if="details.cancellation_settings.operation_notes"
              class="col-12"
            >
              <div class="detail-item h-100">
                <div class="text-muted small mb-2">
                  {{ $t("bulk_buy.operation_notes") }}
                </div>
                <div class="small">
                  {{ details.cancellation_settings.operation_notes }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasPricingInfo" class="details-panel">
      <div class="text-uppercase text-muted fw-semibold mb-3 section-label">
        {{ $t("bulk_buy.quoted_pricing") }}
      </div>

      <div class="row g-2">
        <div
          v-for="row in pricingRows"
          :key="row.label"
          class="col-md-6 col-xl-4"
        >
          <div class="detail-item h-100">
            <div class="text-muted small mb-1">
              {{ row.label }}
            </div>
            <div class="fw-semibold">
              {{ row.value }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate, formatMoney } from "@/composables/formatUtils";
import type { BuyAndCancelOrderDetails } from "@/dashboard/inventory_management/common/companyOrderTypes";
import { isAwaitingRfqQuote } from "@/dashboard/inventory_management/common/companyOrderTypes";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import RfqStatusTimeline from "./RfqStatusTimeline.vue";

const { t } = useI18n();

interface Props {
  details: BuyAndCancelOrderDetails;
  /** Order creation timestamp; drives the RFQ timeline when the price is not yet quoted. */
  orderCreatedAt?: string | null;
  orderStatus?: string | null;
}

const props = defineProps<Props>();

const showRfqTimeline = computed(
  () => !!props.orderCreatedAt && isAwaitingRfqQuote(props.orderStatus)
);

const hasPricingInfo = computed(() => {
  return (
    props.details.quoted_total_price !== null &&
    props.details.quoted_total_price !== undefined
  );
});

const pricePerMwh = computed(() => {
  const totalPrice = Number(props.details.quoted_total_price);
  if (!hasPricingInfo.value || !totalPrice || props.details.volume <= 0) {
    return "—";
  }

  const perMwh = totalPrice / props.details.volume;

  return perMwh.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
});

const matchingLabel = computed(() => {
  if (props.details.global_settings.hourly_matching) {
    return t("bulk_buy.matching_mode.hourly");
  }

  if (props.details.global_settings.monthly_matching) {
    return t("bulk_buy.matching_mode.monthly");
  }

  return t("bulk_buy.matching_mode.yearly");
});

const purchaseSettingItems = computed(() => {
  return [
    {
      label: t("bulk_buy.purchase_mode_label"),
      value: formatPurchaseMode(props.details.global_settings.purchase_mode),
    },
    {
      label: t("bulk_buy.vintage"),
      value: props.details.global_settings.vintage || "—",
    },
    {
      label: t("bulk_buy.eac_source"),
      value: formatEnumLabel(props.details.global_settings.eac_source),
    },
    {
      label: t("bulk_buy.technology"),
      value: formatEnumLabel(props.details.global_settings.technology),
    },
    {
      label: t("bulk_buy.ecolabel"),
      value: formatEnumLabel(props.details.global_settings.ecolabel),
    },
    {
      label: t("bulk_buy.support"),
      value: formatEnumLabel(props.details.global_settings.support),
    },
    {
      label: t("bulk_buy.cod_requirement"),
      value: formatEnumLabel(props.details.global_settings.cod_requirement),
    },
    {
      label: t("bulk_buy.matching"),
      value: matchingLabel.value,
    },
  ];
});

const cancellationSettingItems = computed(() => {
  return [
    {
      label: t("bulk_buy.period_start"),
      value: formatDate(props.details.cancellation_settings.period_start),
    },
    {
      label: t("bulk_buy.period_end"),
      value: formatDate(props.details.cancellation_settings.period_end),
    },
    {
      label: t("bulk_buy.beneficiary_type"),
      value: formatBeneficiaryType(
        props.details.cancellation_settings.beneficiary_type
      ),
    },
    {
      label: t("bulk_buy.cancellation_purpose"),
      value: formatCancellationPurpose(
        props.details.cancellation_settings.cancellation_purpose
      ),
    },
  ];
});

const pricingRows = computed(() => {
  const rows: Array<{ label: string; value: string }> = [
    {
      label: t("bulk_buy.volume"),
      value: `${props.details.volume} MWh`,
    },
    {
      label: t("bulk_buy.total_price"),
      value: `€${formatCurrency(props.details.quoted_total_price, 2)}`,
    },
    {
      label: t("bulk_buy.price_per_mwh"),
      value: `€${pricePerMwh.value}/MWh`,
    },
  ];

  if (props.details.quoted_base_price != null) {
    rows.push({
      label: t("bulk_buy.base_price"),
      value: `€${formatCurrency(props.details.quoted_base_price, 4)}/MWh`,
    });
  }

  if (props.details.quoted_brokerage_fee != null) {
    rows.push({
      label: t("bulk_buy.brokerage_fee"),
      value: `€${formatCurrency(props.details.quoted_brokerage_fee, 4)}/MWh`,
    });
  }

  Object.entries(props.details.quoted_premiums ?? {}).forEach(
    ([key, amount]) => {
      if (Number(amount) > 0) {
        rows.push({
          label: formatEnumLabel(String(key)),
          value: `€${formatCurrency(Number(amount), 4)}/MWh`,
        });
      }
    }
  );

  if (
    props.details.quoted_minimum_cancellation_fee != null &&
    Number(props.details.quoted_minimum_cancellation_fee) >
      Number(props.details.quoted_cancellation_fee ?? 0) * props.details.volume
  ) {
    rows.push({
      label: t("bulk_buy.minimum_cancellation_fee"),
      value: `€${formatCurrency(props.details.quoted_minimum_cancellation_fee, 2)}`,
    });
  } else if (props.details.quoted_cancellation_fee != null) {
    rows.push({
      label: t("bulk_buy.cancellation_fee"),
      value: `€${formatCurrency(props.details.quoted_cancellation_fee, 4)}/MWh`,
    });
  }

  if (props.details.pricing_request_id) {
    rows.push({
      label: t("bulk_buy.quote_reference_label"),
      value: props.details.pricing_request_id.slice(0, 8).toUpperCase(),
    });
  }

  return rows;
});

const PURCHASE_MODE_LABELS: Record<string, string> = {
  CHEAPEST: "bulk_buy.purchase_mode.CHEAPEST",
  GHG: "bulk_buy.purchase_mode.GHG",
  RE100: "bulk_buy.purchase_mode.RE100",
  ADVANCED: "bulk_buy.purchase_mode.ADVANCED",
};

const BENEFICIARY_TYPE_LABELS: Record<string, string> = {
  CONSUMER: "bulk_buy.beneficiary_types.CONSUMER",
  ELECTRICITY_SELLER: "bulk_buy.beneficiary_types.ELECTRICITY_SELLER",
  CONVERSION_PLANT_OPERATOR:
    "bulk_buy.beneficiary_types.CONVERSION_PLANT_OPERATOR",
};

const CANCELLATION_PURPOSE_LABELS: Record<string, string> = {
  DISCLOSURE: "bulk_buy.cancellation_purposes.DISCLOSURE",
  GRID_LOSSES: "bulk_buy.cancellation_purposes.GRID_LOSSES",
  SELF_CONSUMPTION: "bulk_buy.cancellation_purposes.SELF_CONSUMPTION",
  SUPPORT_SCHEME: "bulk_buy.cancellation_purposes.SUPPORT_SCHEME",
  OTHER: "bulk_buy.cancellation_purposes.OTHER",
};

const formatPurchaseMode = (value: string | undefined): string => {
  if (!value) return "—";
  const key = PURCHASE_MODE_LABELS[value];
  return key ? t(key) : formatEnumLabel(value);
};

const formatBeneficiaryType = (value: string | undefined): string => {
  if (!value) return "—";
  const key = BENEFICIARY_TYPE_LABELS[value];
  return key ? t(key) : formatEnumLabel(value);
};

const formatCancellationPurpose = (value: string | undefined): string => {
  if (!value) return "—";
  const key = CANCELLATION_PURPOSE_LABELS[value];
  return key ? t(key) : formatEnumLabel(value);
};

// Prices arrive as decimal strings.
const formatCurrency = (
  value: string | number | null | undefined,
  precision = 2
): string => {
  if (value === null || value === undefined) {
    return "—";
  }

  return formatMoney(Number(value), precision);
};

const formatEnumLabel = (value: string | undefined): string => {
  if (!value || value === "ANY" || value === "NONE") return "—";
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
</script>

<style scoped lang="scss">
@use "../order-details-shared";

.details-panel {
  height: 100%;
}

.details-panel-summary {
  background: var(--phoenix-primary-bg-subtle);
  border-color: var(--phoenix-primary-border-subtle);
}

.summary-detail-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--phoenix-primary-text-emphasis);
}
</style>
